import React from 'react';
import { Button } from '@progress/kendo-react-buttons';

import { TeamContext } from '../../contexts/team';
import { PlayerContext } from '../../contexts/player';
import { GameDocumentContext } from '../../contexts/game-document';
import { DisplayLanguageContext } from '../../contexts/display-languages';
import { generateTitleById } from '../../utils/game-document/display-languages';
import { GameContext } from '../../contexts/game';
import {
  PlayerChatContext,
  PlayerChatContextProps
} from '../../contexts/player-chat';
import { PlayerState } from '../../types/state';
import { NotificationIndicator } from '../notification-indicator';

interface CommonTabProps {
  activeTab: string;
  otherPlayers: PlayerState[];

  handleSwitch: (chatType: string) => void;
  handleSwitchToDirectChat: () => void;
  chatTypeText: (type: string) => string;
}

const ButtonContent = ({
  text,
  newChatCount
}: {
  text: string;
  newChatCount: number;
}) => {
  return (
    <div className={'d-flex align-items-center gap-1'}>
      {text}
      {newChatCount > 0 && (
        <NotificationIndicator counter={newChatCount} bgColor={'#FF5527'} />
      )}
    </div>
  );
};

const CommonTab: React.FC<CommonTabProps> = ({
  activeTab,
  otherPlayers,
  handleSwitch,
  chatTypeText,
  handleSwitchToDirectChat
}) => {
  const { newChat } = React.useContext(
    PlayerChatContext
  ) as PlayerChatContextProps;
  const [gameDocumentstate] = React.useContext(GameDocumentContext);
  const [game] = React.useContext(GameContext);
  const [displayLanguageContext] = React.useContext(DisplayLanguageContext);
  const [playerState] = React.useContext(PlayerContext);
  const [teamState] = React.useContext(TeamContext);

  const isInLobby = ['waiting', 'ready'].includes(
    playerState.playerState?.status ?? ''
  );
  const preGameSetting = gameDocumentstate.gameDocument?.settings.preGame;
  const inGameSetting =
    game.gameState?.runtime?.inGame ??
    gameDocumentstate.gameDocument?.settings.inGame;

  const showAllPlayerChat = isInLobby
    ? preGameSetting?.showPreGameAllPlayerChat
    : inGameSetting?.showInGameAllPlayerChat;
  const showFacilitatorChat = isInLobby
    ? preGameSetting?.showPreGameFacilitatorChat
    : inGameSetting?.showInGameFacilitatorChat;
  const showTeamChat = isInLobby
    ? preGameSetting?.showPreGameTeamChat
    : inGameSetting?.showInGameTeamChat;
  const showGroupChat = isInLobby
    ? preGameSetting?.showPreGameGroupChat
    : inGameSetting?.showInGameGroupChat;
  const showDirectChat = isInLobby
    ? preGameSetting?.showPreGameDirectChat
    : inGameSetting?.showInGameDirectChat;

  const teamCode = playerState.playerState?.teamCode;
  const groupCode =
    teamState.teamState?.groupCode !== '00000000-0000-0000-0000-000000000000'
      ? teamState.teamState?.groupCode
      : undefined;

  const newChatCount = React.useCallback(
    (
      tabType:
        | 'global-chat'
        | 'group-chat'
        | 'team-chat'
        | 'player-chat'
        | 'facilitator-chat'
    ) => newChat.filter((c) => c.group === tabType).length,
    [newChat.length]
  );

  return (
    <div className="d-none d-md-flex align-items-center gap-3 mb-3">
      <span className="text-dark m-0">
        {generateTitleById(
          'e20069a1-1a00-4183-b62e-7375d02b4721',
          gameDocumentstate,
          displayLanguageContext.displayLanguageSelected.resources!,
          'game'
        ) || 'Chat with'}
      </span>
      <div className="example-wrapper">
        {showAllPlayerChat && (
          <Button
            className={'mr-1'}
            onClick={() => handleSwitch('All Player')}
            themeColor={activeTab === 'All Player' ? 'dark' : 'light'}>
            {ButtonContent({
              text: chatTypeText('All Player'),
              newChatCount: newChatCount('global-chat')
            })}
          </Button>
        )}

        {showGroupChat && groupCode && (
          <Button
            className={'mr-1'}
            onClick={() => handleSwitch('Group')}
            themeColor={activeTab === 'Group' ? 'dark' : 'light'}>
            {ButtonContent({
              text: chatTypeText('Group'),
              newChatCount: newChatCount('group-chat')
            })}
          </Button>
        )}

        {showTeamChat && teamCode && (
          <Button
            className={'mr-1'}
            onClick={() => handleSwitch('Team')}
            themeColor={activeTab === 'Team' ? 'dark' : 'light'}>
            {ButtonContent({
              text: chatTypeText('Team'),
              newChatCount: newChatCount('team-chat')
            })}
          </Button>
        )}

        {showDirectChat && otherPlayers.length > 0 && (
          <Button
            className={'mr-1'}
            onClick={() => {
              handleSwitchToDirectChat();
            }}
            themeColor={activeTab === 'Direct' ? 'dark' : 'light'}>
            {ButtonContent({
              text: chatTypeText('Direct'),
              newChatCount: newChatCount('player-chat')
            })}
          </Button>
        )}

        {showFacilitatorChat && (
          <Button
            className={'mr-1'}
            onClick={() => handleSwitch('Facilitators')}
            themeColor={activeTab === 'Facilitators' ? 'dark' : 'light'}>
            {ButtonContent({
              text: chatTypeText('Facilitators'),
              newChatCount: newChatCount('facilitator-chat')
            })}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CommonTab;
